import { useState } from 'react';
import { Box, Typography, TextField, Button, LinearProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { updateNewUser } from '../../state/user/userSlice';
import { RootState } from '../../app/store';
import { useTranslation } from 'react-i18next';

function calculatePasswordStrength(password?: string) {
  if (!password) {
    return { label: 'very_weak', strengthPercent: 0 };
  }

  let score = 0;
  if (password.length > 6) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  let labelKey: string;
  switch (score) {
    case 0:
      labelKey = 'very_weak';
      break;
    case 1:
      labelKey = 'weak';
      break;
    case 2:
      labelKey = 'medium';
      break;
    case 3:
      labelKey = 'strong';
      break;
    case 4:
      labelKey = 'very_strong';
      break;
    default:
      labelKey = 'weak';
  }

  const strengthPercent = (score / 4) * 100;

  return { label: labelKey, strengthPercent };
}

interface SignUpSectionProps {
  onSubmit: () => void;
}

const SignUpSection = ({ onSubmit }: SignUpSectionProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const { newUser: user, onboardingLoading, onboardingError } = useSelector((state: RootState) => state.user);

  const { label: passwordLabelKey, strengthPercent } = calculatePasswordStrength(user.password);
  const passwordLabel = t(`signup_section.password.${passwordLabelKey}`);

  const isPasswordAcceptable = ['medium', 'strong', 'very_strong'].includes(passwordLabelKey);

  function createPasswordStrengthLabel(passwordLabel: string): string {
    return `${t('signup_section.password.password_strength')}: ${passwordLabel}`;
  }

  return (
    <Box mb={4}>
      <Box mt={2} display="flex" flexDirection="column" gap={2} maxWidth={400}>
        <TextField 
          label={t('signup_section.your_email')}
          type='email'
          variant="outlined" 
          value={user.email || ''} 
          onChange={(e) => dispatch(updateNewUser({name: 'email', value: e.target.value}))}
        />
        
        <TextField
          label={t('signup_section.password.password')}
          variant="outlined"
          type="password"
          value={user.password || ''}
          onChange={(e) => dispatch(updateNewUser({name: 'password', value: e.target.value}))}
          helperText={createPasswordStrengthLabel(passwordLabel)}
        />
        
        {user.password && (
          <Box display="flex" alignItems="center" gap={2}>
            <Box width="100%">
              <LinearProgress 
                variant="determinate" 
                value={strengthPercent} 
                sx={{
                  height: 8,
                  borderRadius: 4,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor:
                      strengthPercent >= 75
                        ? 'green'
                        : strengthPercent >= 50
                        ? 'orange'
                        : 'red'
                  }
                }} 
              />
            </Box>
            <Typography variant="body2" color="textSecondary">
              {passwordLabel}
            </Typography>
          </Box>
        )}

        <Button 
          variant="contained" 
          color="primary" 
          onClick={onSubmit} 
          disabled={!isPasswordAcceptable}
        >
          {onboardingLoading ? t('signup_section.submitting') : t('signup_section.save_my_progress')}
        </Button>
      </Box>
    </Box>
  );
};

export default SignUpSection;
